import React, { useState, useContext, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Button, Container, Typography } from '@mui/material';
import {
  VehicleDetails,
  CustomerGroups,
  VehicleActivation,
  VehicleConnections,
} from 'components/Vehicles/VehiclesWizard';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { UserContext } from 'context';
import Layout from 'components/Layout';
import Wizard from 'components/UI/Wizard';
import { uiActions } from 'state/modules/UI';
import { vehicleActions } from 'state/modules/vehicles';
import { withRouter, useParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
// TODO replace by StyledStepperContainer
import useStyles from './index.styled';
// import { styled } from '@mui/material';
// const StyledStepperContainer = styled(Box)({
//   margin: '0 auto',
//   maxWidth: '800px',
// });

const VehiclesOnboarding = ({ history }) => {
  const { orgType } = useContext(UserContext);
  const classes = useStyles();
  const { id } = useParams();
  const [showSaveAndExitBtn, setShowSaveAndExitBtn] = useState(false);
  const query = new URLSearchParams(useLocation().search);
  const step = query?.get('step');
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const successMessage = `Vehicle ${id ? 'updated' : 'created'} successfully`;

  const stepsList = [
    {
      position: 1,
      key: 'details',
      label: i18n.t('vehicle.details_title'),
    },
    {
      position: 2,
      key: 'connection',
      label: i18n.t('vehicle.connection'),
    },
  ];

  if (orgType !== 'msOwnerOrg') {
    stepsList.push(
      {
        position: 3,
        key: 'customer_groups',
        label: i18n.t('vehicle.vehicle_groups'),
      },
      {
        position: 4,
        key: 'activation',
        label: i18n.t('vehicle.activation'),
      }
    );
  }

  const {
    loading,
    onboarding: { exitStep, position, exitOnboarding, errors, steps, lastStep },
  } = useSelector((state) => state.vehicles);

  const formRef = useRef(null);

  const dispatch = useDispatch();

  /**
   * Set title of the page
   * Validate type of service group
   */
  const setTitle = (vehicle) => {
    if (id && vehicle) {
      return `Vehicle ID #${vehicle?.id}`;
    } else if (id) {
      return i18n.t('wizard.titles.update_vehicle');
    }
    return i18n.t('wizard.titles.create_vehicle');
  };

  const onPrevious = () => {
    dispatch(vehicleActions.changePosition('prev'));
  };

  const handleNextPosition = () => {
    dispatch(vehicleActions.changePosition('next'));
  };

  const handleEndOnboarding = (ev, type) => {
    if (formRef?.current && type !== 'cancel') {
      return formRef.current.submitForm();
    }
    onExitOnboarding(type);
  };

  const onExitOnboarding = (type) => {
    dispatch(vehicleActions.resetOnboarding());
    if (type !== 'cancel') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: successMessage,
        })
      );
    }
    if (id) {
      history.push(buildUrl(`/vehicles/${id}`));
    } else {
      history.push(buildUrl('/vehicles'));
    }
  };

  const handleNextStep = (exit = false) => {
    switch (position) {
      case 0:
        if (orgType !== 'msOwnerOrg' && exit) {
          handleEndOnboarding(exit);
        } else if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      case 1:
        if (exit || orgType === 'msOwnerOrg') {
          handleEndOnboarding(exit);
        } else {
          handleNextPosition(exit);
        }
        break;
      case 2:
        if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      case 3:
        if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      default:
        break;
    }
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(vehicleActions.setExit(exit || !exitStep, 'exitStep'));
  };

  const handleShowOnSaveAndExitModal = () => {
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  const handleOnSaveAndExit = () => {
    handleExit(true);
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (id) {
      dispatch(vehicleActions.fetchVehicleById(id, 'onboarding'));
    }
  }, [id, dispatch]);

  useEffect(() => {
    setShowSaveAndExitBtn(false);
  }, [position]);

  useEffect(() => {
    if (step) {
      dispatch(vehicleActions.changeStep(step));
    }
  }, [step, dispatch]);

  useEffect(() => {
    if (
      errors &&
      lastStep &&
      (Array.isArray(errors) ||
        Object.keys(errors).length !== 0 ||
        Array.isArray(steps?.details?.vehicle_association_requirements))
    ) {
      handleEndOnboarding();
    } else if (lastStep) {
      displaySuccessModal();
    }
  }, [lastStep]); // eslint-disable-line

  useEffect(() => {
    if (exitOnboarding) {
      onExitOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  const displaySuccessModal = () => {
    handleEndOnboarding();
  };

  const vehicleWizard = () => {
    return (
      <Box>
        <Wizard
          mode="create"
          exit={exitStep}
          steps={stepsList}
          loading={loading}
          title={setTitle(steps?.details)}
          position={position}
          handleExit={handleExit}
          onNext={handleNextStep}
          onPrevious={onPrevious}
          onSaveAndExit={onSaveAndExit}
          successMessage={successMessage}
          handleEndOnboarding={handleEndOnboarding}
          showSaveAndExitModal={showSaveAndExitModal}
          customStepperClass={classes.stepperContainer}
          handleShowOnSaveAndExitModal={handleShowOnSaveAndExitModal}
        >
          {position === 0 && (
            <VehicleDetails
              exit={exitStep}
              errors={errors}
              formRef={formRef}
              onSave={setShowSaveAndExitBtn}
              step={steps.details}
              handleNextPosition={handleNextPosition}
            />
          )}
          {position === 1 && (
            <VehicleConnections currentVehicle={steps.details} step={steps.connection} />
          )}
          {position === 2 && orgType && orgType !== 'msOwnerOrg' && (
            <CustomerGroups
              exit={exitStep}
              errors={errors}
              formRef={formRef}
              onSave={setShowSaveAndExitBtn}
              step={steps.details}
              handleNextPosition={handleNextPosition}
            />
          )}
          {orgType !== 'msOwnerOrg' && position === 3 && (
            <VehicleActivation
              exit={exitStep}
              errors={errors}
              formRef={formRef}
              onSave={setShowSaveAndExitBtn}
              currentVehicle={steps.details}
              handleNextPosition={handleNextPosition}
              displaySuccessModal={onExitOnboarding}
            />
          )}
        </Wizard>
      </Box>
    );
  };

  return (
    <Layout title="Dashboard">
      <Container maxWidth="lg">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={{ xs: 12 }}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 6 }}
            >
              <Grid>
                <Typography variant="h6" gutterBottom>
                  <Box sx={{ fontWeight: 'fontWeightBold', fontSize: 24, mb: 1 }}>{setTitle()}</Box>
                </Typography>
              </Grid>
              {showSaveAndExitBtn && (
                <Grid>
                  <Button
                    onClick={handleOnSaveAndExit}
                    variant="contained"
                    sx={{
                      textTransform: 'capitalize',
                    }}
                    color="primary"
                  >
                    {i18n.t('wizard.save_and_exit')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>{vehicleWizard()}</Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

VehiclesOnboarding.propTypes = {
  history: PropTypes.object.isRequired,
};

export default withRouter(VehiclesOnboarding);
